<template>
    <pc-capsule
        v-if="!groupInstance.IsArchived || showArchived"
        v-model:shown="expanded"
        v-bind:has-body="true">
        <template v-slot:capsule-header>
            <div class="d-flex align-items-center">
                {{ groupLabel }}
                <div
                    v-if="groupActionType !== ACTION_TYPE.NONE"
                    class="ms-auto px-1 clickable-div"
                    v-bind:title="groupActionTooltipText"
                    v-on:click.stop="doGroupAction">
                    <pc-bs-icon v-bind:name="groupActionIcon" />
                </div>
            </div>
        </template>
        <div class="py-2 px-3 vgap-3">
            <template
                v-for="slotSchema in visibleSlotSchemas"
                v-bind:key="slotSchema.PublicId">
                <member-multi-instance
                    v-if="slotSchema.IsMultiInstance"
                    v-bind:slot-instance="getSlotInstanceBySlotId(slotSchema.SlotId)"
                    v-bind:group-instance="groupInstance"
                    v-bind:slot-schema="slotSchema" />
                <member-single-instance
                    v-for="member in getSlotInstanceBySlotId(slotSchema.SlotId).Members"
                    v-else
                    v-bind:key="member.ConnectionMemberId"
                    v-bind:member="member"
                    v-bind:slot-schema="slotSchema" />
            </template>
        </div>
    </pc-capsule>
</template>

<script setup>
    import { computed, inject, ref } from "vue";
    import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
    import memberMultiInstance from "./memberMultiInstance.vue";
    import memberSingleInstance from "./memberSingleInstance.vue";
    import { useChannelStore } from "./channelStore.js";
    import { storeToRefs } from "pinia";
    import { NEW_GROUP_ID_BASE } from "Types/channelTypes.js";
    import { KEYS, NAMESPACE } from "../i18n/programChannels.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { KEYS as ARCHIVE_KEYS, NAMESPACE as ARCHIVE_NAMESPACE } from "Util/i18n/archiveUtil.i18n";
    import { useResource } from "Util/i18n";

    const app = inject(PAGE_APP_SYMBOL);
    const channelStore = useChannelStore();
    const { channelInUse, isEditingSchema, showArchived } = storeToRefs(channelStore);
    const { setGroupArchived, removeGroup } = channelStore;

    const { singleInstanceGroupLabel } = useResource(KEYS, NAMESPACE);
    const { deleteText } = useResource(COMMON_KEYS, COMMON_NAMESPACE);
    const { archiveText, unarchiveText } = useResource(ARCHIVE_KEYS, ARCHIVE_NAMESPACE);

    const ACTION_TYPE = {
        DELETE: "delete",
        ARCHIVE: "archive",
        UNARCHIVE: "unarchive",
        NONE: "none"
    };

    const props = defineProps({
        groupSchema: { type: Object, required: true },
        groupInstance: { type: Object, required: true },
        index: { type: Number, required: true }
    });

    const expanded = ref(true);

    const visibleSlotSchemas = computed(() => props.groupSchema.Slots.filter((slot) => slot.IsVisible));

    const groupLabel = computed(() => {
        if (props.groupSchema.IsMultiInstance) {
            let label = props.groupSchema.InstancePrefix + " " + (props.index + 1);
            if (props.groupInstance.IsArchived) {
                label += " (Archived)";
            }
            return label;
        } else if (props.groupSchema.InstancePrefix) {
            return props.groupSchema.InstancePrefix;
        }
        return singleInstanceGroupLabel.value;
    });

    function getSlotInstanceBySlotId(slotId) {
        for (const slot of props.groupInstance.Slots) {
            if (slot.SlotId == slotId) {
                return slot;
            }
        }
        return {};
    }

    const groupActionType = computed(() => {
        if (!props.groupSchema.IsMultiInstance) {
            return ACTION_TYPE.NONE;
        }
        if (!channelInUse.value || props.groupInstance.PublicId.startsWith(NEW_GROUP_ID_BASE)) {
            return ACTION_TYPE.DELETE;
        }
        if (props.groupSchema.CanArchiveFields && isEditingSchema.value) {
            return props.groupInstance.IsArchived ? ACTION_TYPE.UNARCHIVE : ACTION_TYPE.ARCHIVE;
        }
        return ACTION_TYPE.NONE;
    });

    const groupActionIcon = computed(() => {
        switch (groupActionType.value) {
            case ACTION_TYPE.ARCHIVE:
                return "box-arrow-in-down";
            case ACTION_TYPE.UNARCHIVE:
                return "box-arrow-up";
            case ACTION_TYPE.DELETE:
                return "x-lg";
        }
        return "";
    });

    const groupActionTooltipText = computed(() => {
        switch (groupActionType.value) {
            case ACTION_TYPE.ARCHIVE:
                return archiveText.value;
            case ACTION_TYPE.UNARCHIVE:
                return unarchiveText.value;
            case ACTION_TYPE.DELETE:
                return deleteText.value;
        }
        return "";
    });

    function doGroupAction() {
        app.setUnsavedChanges(true);
        switch (groupActionType.value) {
            case ACTION_TYPE.ARCHIVE:
                setGroupArchived(props.groupInstance.PublicId, true);
                return;
            case ACTION_TYPE.UNARCHIVE:
                setGroupArchived(props.groupInstance.PublicId, false);
                return;
            case ACTION_TYPE.DELETE:
                removeGroup(props.groupInstance.PublicId);
                return;
        }
    }
</script>

<style lang="scss" scoped>
    .clickable-div:hover {
        cursor: pointer;
        background: var(--bs-gray-100);
        border-radius: 50%;
        box-shadow: 0 0 3px 3px var(--bs-gray-100);
    }
</style>
